// Host-agnostic chat container. No Office.js imports here — Outlook specifics
// live in src/office/outlook.ts and are passed in via the seed effect below.
// This same component re-mounts unchanged inside an Outlook task pane, a Teams
// tab, or the standalone web page.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../hooks/useChat';
import { isOutlook, readOpenEmail, emailToSeed } from '../office/outlook';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';
import { KnowledgeMap } from './KnowledgeMap';

export function ChatWidget() {
  const chat = useChat();
  const seedRef = useRef<string | null>(null);
  const seededOnce = useRef(false);

  // Inside Outlook: read the open email once and switch to Concept mode,
  // pre-filling the composer with the email so staff can draft a reply.
  useEffect(() => {
    if (!isOutlook()) return;
    readOpenEmail().then((email) => {
      if (!email) return;
      seedRef.current = emailToSeed(email);
      if (!seededOnce.current && !chat.input) {
        seededOnce.current = true;
        chat.setMode('draft');
        chat.setInput(seedRef.current);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user switches to Concept and the box is empty, offer the seed.
  useEffect(() => {
    if (chat.mode === 'draft' && !chat.input && seedRef.current) {
      chat.setInput(seedRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.mode]);

  const [showMap, setShowMap] = useState(false);
  // Starting a new chat clears the screen and there is no way back to the old
  // one from the UI yet, so confirm first — but only when there is something to
  // lose. Nagging on an empty chat trains people to click through the warning.
  const [confirmNew, setConfirmNew] = useState(false);

  function startNewChat() {
    if (chat.messages.length === 0) {
      chat.newChat();
      return;
    }
    setConfirmNew(true);
  }

  // source_ref values from the most recent answer — the chunks the map lights up.
  const lastRefs = useMemo(() => {
    const withSources = [...chat.messages].reverse().find((m) => m.sources?.length);
    return withSources?.sources?.map((s) => s.ref) ?? [];
  }, [chat.messages]);

  return (
    <div className="widget">
      <header className="topbar">
        <div className="brand">
          <span className="logo" aria-hidden>G</span>
          <span className="brand-title">Nina · Kennis Agent</span>
        </div>
        <div className="topbar-actions">
          <button className="mapbtn" onClick={() => setShowMap((v) => !v)} aria-pressed={showMap}>
            {showMap ? 'Verberg kaart' : 'Kenniskaart'}
          </button>
          {confirmNew ? (
            <span className="newchat-confirm">
              <span className="newchat-ask">Huidig gesprek sluiten?</span>
              <button
                className="newchat-yes"
                onClick={() => { chat.newChat(); setConfirmNew(false); }}
              >
                Ja
              </button>
              <button className="newchat-no" onClick={() => setConfirmNew(false)}>
                Annuleren
              </button>
            </span>
          ) : (
            <button className="newchat" onClick={startNewChat}>+ Nieuw chat</button>
          )}
        </div>
      </header>

      <div className="dev-banner" role="note">
        <strong>Testversie</strong> — Nina is nog in ontwikkeling. Controleer
        antwoorden voordat je ze gebruikt en meld fouten aan Nina AI.
      </div>

      {showMap && <KnowledgeMap highlightRefs={lastRefs} />}
      <MessageList
        messages={chat.messages}
        loading={chat.loading}
        company={chat.company}
        division={chat.division}
      />

      <ChatInput
        value={chat.input}
        onChange={chat.setInput}
        onSend={() => chat.send(chat.input)}
        onUpload={chat.upload}
        loading={chat.loading}
        mode={chat.mode}
      />

      <footer className="tagline">Van afval naar product.</footer>
    </div>
  );
}
